// Every table an app can sync, by the name PowerSync gives it locally (the sync-rules
// output name): its zod row and the sync-api route local writes upload to.
import type { z } from "zod";
import * as s from "./schemas/index.js";

export const TABLES = {
  // core
  stores: [s.storeSchema, "/core/stores"],
  businesses: [s.businessSchema, "/core/businesses"],
  business_users: [s.businessUserSchema, "/core/business-users"],
  devices: [s.deviceSchema, "/core/devices"],
  staff: [s.staffSchema, "/core/staff"],
  store_payment_methods: [s.storePaymentMethodSchema, "/core/store-payment-methods"],
  staff_sessions: [s.staffSessionSchema, "/core/staff-sessions"],
  roles: [s.roleSchema, "/core/roles"],
  staff_shifts: [s.staffShiftSchema, "/core/staff-shifts"],
  payroll_runs: [s.payrollRunSchema, "/core/payroll-runs"],
  bonus_rules: [s.bonusRuleSchema, "/core/bonus-rules"],
  bonus_awards: [s.bonusAwardSchema, "/core/bonus-awards"],
  business_integrations: [s.businessIntegrationSchema, "/core/business-integrations"],
  // catalog
  categories: [s.categorySchema, "/catalog/categories"],
  products: [s.productSchema, "/catalog/products"],
  product_variant_groups: [s.productVariantGroupSchema, "/catalog/product-variant-groups"],
  product_variant_options: [s.productVariantOptionSchema, "/catalog/product-variant-options"],
  add_ons: [s.addOnSchema, "/catalog/add-ons"],
  product_variants: [s.productVariantSchema, "/catalog/product-variants"],
  store_inventory: [s.storeInventorySchema, "/catalog/store-inventory"],
  inventory_adjustments: [s.inventoryAdjustmentSchema, "/catalog/inventory-adjustments"],
  ingredients: [s.ingredientSchema, "/catalog/ingredients"],
  product_ingredients: [s.productIngredientSchema, "/catalog/product-ingredients"],
  suppliers: [s.supplierSchema, "/catalog/suppliers"],
  purchase_orders: [s.purchaseOrderSchema, "/catalog/purchase-orders"],
  purchase_order_items: [s.purchaseOrderItemSchema, "/catalog/purchase-order-items"],
  stock_transfers: [s.stockTransferSchema, "/catalog/stock-transfers"],
  // sales
  orders: [s.orderSchema, "/sales/orders"],
  order_items: [s.orderItemSchema, "/sales/order-items"],
  order_item_option_selections: [s.orderItemOptionSelectionSchema, "/sales/order-item-option-selections"],
  kitchen_tickets: [s.kitchenTicketSchema, "/sales/kitchen-tickets"],
  drawer_closes: [s.drawerCloseSchema, "/sales/drawer-closes"],
  // floor
  tables: [s.tableSchema, "/floor/tables"],
  reservations: [s.reservationSchema, "/floor/reservations"],
  table_sessions: [s.tableSessionSchema, "/floor/table-sessions"],
  // crm
  members: [s.memberSchema, "/crm/members"],
  point_transactions: [s.pointTransactionSchema, "/crm/point-transactions"],
  business_loyalty_settings: [s.businessLoyaltySettingSchema, "/crm/business-loyalty-settings"],
  member_store_points: [s.memberStorePointSchema, "/crm/member-store-points"],
  loyalty_configs: [s.loyaltyConfigSchema, "/crm/loyalty-configs"],
  rewards: [s.rewardSchema, "/crm/rewards"],
  vouchers: [s.voucherSchema, "/crm/vouchers"],
  promos: [s.promoSchema, "/crm/promos"],
  // services
  services: [s.serviceSchema, "/services/services"],
  bookings: [s.bookingSchema, "/services/bookings"],
  service_staff: [s.serviceStaffSchema, "/services/service-staff"],
  clients: [s.clientSchema, "/services/clients"],
  memberships: [s.membershipSchema, "/services/memberships"],
  service_loyalty_configs: [s.serviceLoyaltyConfigSchema, "/services/loyalty-configs"],
  // platform
  payment_method_configs: [s.paymentMethodConfigSchema, "/platform/payment-method-configs"],
  audit_log: [s.auditLogSchema, "/platform/audit-log"],
  subscription_plans: [s.subscriptionPlanSchema, "/platform/subscription-plans"],
  billing_cycles: [s.billingCycleSchema, "/platform/billing-cycles"],
  module_presets: [s.modulePresetSchema, "/platform/module-presets"],
  subscription_payments: [s.subscriptionPaymentSchema, "/platform/subscription-payments"],
  subscription_discounts: [s.subscriptionDiscountSchema, "/platform/subscription-discounts"],
  discount_codes: [s.discountCodeSchema, "/platform/discount-codes"],
  // printing
  print_hubs: [s.printHubSchema, "/printing/print-hubs"],
  printers: [s.printerSchema, "/printing/printers"],
  print_jobs: [s.printJobSchema, "/printing/print-jobs"],
} as const satisfies Record<string, readonly [z.AnyZodObject, string]>;

export type TableName = keyof typeof TABLES;
/** The parsed row type of a local table. */
export type RowOf<N extends TableName> = z.infer<(typeof TABLES)[N][0]>;
